import posthog from "posthog-js";
import axios, { AxiosInstance } from "axios";
import { firebaseAuth } from "@/firebase/auth";
import type { CombinedMetrics, GaBehavioralMetrics, FacebookDetails } from "@/types/general";
import type {
  ResponseAPIJSON,
  BrandProductsReportResponse,
  ProductAnalysisResponse,
  AudienceResponse
} from "@/types/responses";
import type { AltTextAssessResponse } from "@/types/responses/altTextAssess";
import type {
  CustomerSnapshot,
  PerformanceMetrics,
  MetaAd,
  FacebookAdsAccount,
  FacebookPost,
  InstagramPost,
  ProductMetricsResponse
} from "@/types/responses/eCommerceMetrics";
import type { NewAudience, ShopifyStore } from "@/types/general";
import type { ProductAnalysisRequest, WooCommerceStoreRequest } from "@/types/requests";
import type {
  BasicSuccessResponse,
  WooCommerceStore,
  FacebookAccount,
  GoogleAnalyticsAccount
} from "@/types/responses/eCommerceAuth";
import type { MagentoStore } from "@/types/responses/eCommerceMetrics";
import type { EntityAnalysisResponse } from "@/types/responses/entityAnalysis";
import type {
  AdminUsersListResponse,
  AdminUserDetailsResponse,
  AdminDeleteUserResponse
} from "@/types/responses/admin";

export interface ApiResponse<T = unknown> {
  data: T;
  meta?: Record<string, unknown>;
  errors?: Array<{
    status: string;
    code: string;
    detail: string;
  }>;
}

export class ApiClient {
  private axios: AxiosInstance;

  constructor(baseUrl: string) {
    this.axios = axios.create({
      baseURL: baseUrl,
      headers: {
        "Content-Type": "application/vnd.api+json",
        Accept: "application/vnd.api+json"
      }
    });

    this.axios.interceptors.request.use(async config => {
      const user = firebaseAuth.getCurrentUser();

      if (user) {
        try {
          let token: string;

          if (await firebaseAuth.isTokenExpiringSoon(5)) {
            console.log("Token expiring soon, refreshing...");
            token = await firebaseAuth.getIdToken(true);
          } else {
            token = await firebaseAuth.getIdToken(false);
          }

          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.error("Failed to get authentication token:", error);
          throw new Error("Authentication failed. Please sign in again.");
        }
      }

      return config;
    });

    // Response interceptor - error handling + logging
    this.axios.interceptors.response.use(
      response => response, // Return full response, consuming code will extract data as needed
      error => {
        // Detect and enhance HTTP 429 (Rate Limit) errors
        if (error.response?.status === 429) {
          const retryAfterHeader = error.response.headers["retry-after"];
          const retryAfterBody = error.response?.data?.errors?.[0]?.meta?.retry_after_seconds;

          error.isRateLimited = true;
          error.retryAfter = parseInt(retryAfterHeader || retryAfterBody || "60", 10);
          error.endpoint = error.config?.url;

          posthog.captureException(error, {
            isBackend: "yes",
            url: error.config?.url,
            isRateLimited: true,
            retryAfter: error.retryAfter
          });

          throw error;
        }

        posthog.captureException(error, { isBackend: "yes", url: error.config?.url });

        const errorMessage =
          error.response?.data?.errors?.[0]?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Request failed";

        throw new Error(errorMessage);
      }
    );
  }

  isAuthenticated(): boolean {
    return !!firebaseAuth.getCurrentUser();
  }

  // Compatibility properties for ApiHTTP interface
  get token(): string | null {
    // This is sync getter, actual token fetching happens in getAuthHeaders()
    return firebaseAuth.getCurrentUser() ? "valid-token" : null;
  }

  // API sections for compatibility with old ApiHTTP interface
  // Note: auth and users are handled by dedicated services (firebaseAuth, userService)

  get brand() {
    return {
      productImages: async (url: string) =>
        (
          await this.axios.get<BrandProductsReportResponse>(
            `/api/products/search?url=${encodeURIComponent(url)}`
          )
        ).data,

      meaningfulness: async (title: string, alt: string) =>
        (
          await this.axios.post<ResponseAPIJSON<{ score: number }>>(
            "/api/text-analysis/v1/alt-text/meaningfulness/score",
            { title, alt }
          )
        ).data,

      assess: async (altText: string, title: string) =>
        (
          await this.axios.get<AltTextAssessResponse>(
            `/api/products/alt-text/assess?alt_text=${encodeURIComponent(altText)}&title=${encodeURIComponent(title)}`
          )
        ).data,

      difficultyRatingPublic: async (text: string) =>
        (
          await this.axios.post<ResponseAPIJSON<{ percentage: number }>>(
            "/api/text-analysis/v1/complexity/percentage",
            { text }
          )
        ).data,

      productAIAnalysis: async (data: ProductAnalysisRequest) =>
        (await this.axios.post<ProductAnalysisResponse>("/api/ai-analysis/description", data)).data
    };
  }

  get audience() {
    return {
      addAudience: async (data: NewAudience) =>
        (
          await this.axios.post<ResponseAPIJSON<AudienceResponse>>(
            "/api/audience/v1/calculate",
            data
          )
        ).data
    };
  }

  get entityAnalysis() {
    return {
      analyzeDescription: async (description: string) =>
        (
          await this.axios.post<EntityAnalysisResponse>("/api/description_analysis", {
            text: description
          })
        ).data
    };
  }

  get eCommerceAuth() {
    return {
      getGa4IntegrationUrl: async () =>
        (
          await this.axios.get<ResponseAPIJSON<{ auth_url: string }>>(
            "/api/ecommerce-auth/v1/ga/auth-url"
          )
        ).data,

      getShopifyStores: async () =>
        (
          await this.axios.get<ResponseAPIJSON<ShopifyStore[]>>(
            "/api/ecommerce-auth/v1/spf/stores?skip=0&limit=100"
          )
        ).data,

      disconnectShopifyStore: async (storeId: string) =>
        (
          await this.axios.delete<ResponseAPIJSON<BasicSuccessResponse>>(
            `/api/ecommerce-auth/v1/spf/disconnect/${storeId}`
          )
        ).data,

      getAuthStore: async (params: string) =>
        (
          await this.axios.get<ResponseAPIJSON<BasicSuccessResponse>>(
            `/api/ecommerce-auth/v1/spf/callback?${params}`
          )
        ).data,

      getMagentoLogin: async (consumerKey: string) =>
        (
          await this.axios.get<ResponseAPIJSON<BasicSuccessResponse>>(
            `/api/ecommerce-auth/v1/magento/login?oauth_consumer_key=${consumerKey}`
          )
        ).data,

      addWooCommerceStore: async (store: WooCommerceStoreRequest) =>
        (
          await this.axios.post<ResponseAPIJSON<WooCommerceStore>>(
            "/api/ecommerce-auth/v1/woo/store",
            store
          )
        ).data,

      getWooCommerceStores: async () =>
        (
          await this.axios.get<ResponseAPIJSON<WooCommerceStore[]>>(
            "/api/ecommerce-auth/v1/woo/stores?skip=0&limit=100"
          )
        ).data,

      getMagentoStores: async () =>
        (
          await this.axios.get<ResponseAPIJSON<MagentoStore[]>>(
            "/api/ecommerce-auth/v1/magento/stores"
          )
        ).data,

      getMetaIntegrationUrl: async () =>
        (
          await this.axios.get<ResponseAPIJSON<{ auth_url: string }>>(
            "/api/ecommerce-auth/v1/meta/auth-url"
          )
        ).data,

      getMetaAccounts: async () =>
        (
          await this.axios.get<ResponseAPIJSON<FacebookAccount[]>>(
            "/api/ecommerce-auth/v1/meta/me/accounts"
          )
        ).data,

      disconnectMeta: async () =>
        (
          await this.axios.delete<ResponseAPIJSON<BasicSuccessResponse>>(
            "/api/ecommerce-auth/v1/meta/disconnect"
          )
        ).data,

      getGa4Accounts: async () =>
        (
          await this.axios.get<ResponseAPIJSON<GoogleAnalyticsAccount[]>>(
            "/api/ecommerce-auth/v1/ga/accounts"
          )
        ).data,

      disconnectGa4: async () =>
        (
          await this.axios.delete<ResponseAPIJSON<BasicSuccessResponse>>(
            "/api/ecommerce-auth/v1/ga/disconnect"
          )
        ).data,

      disconnectWoo: async (storeId: string) =>
        (
          await this.axios.delete<ResponseAPIJSON<BasicSuccessResponse>>(
            `/api/ecommerce-auth/v1/woo/disconnect/${storeId}`
          )
        ).data,

      disconnectMagento: async (storeId: string) =>
        (
          await this.axios.delete<ResponseAPIJSON<BasicSuccessResponse>>(
            `/api/ecommerce-auth/v1/magento/disconnect/${storeId}`
          )
        ).data,

      selectFacebookPage: async (pageId: string) =>
        (
          await this.axios.post<ResponseAPIJSON<BasicSuccessResponse>>(
            `/api/ecommerce-auth/v1/meta/set-default/${pageId}`,
            {}
          )
        ).data,

      selectGa4Page: async (
        accountName: string,
        propertyName: string,
        propertyDisplayName: string
      ) =>
        (
          await this.axios.post<ResponseAPIJSON<BasicSuccessResponse>>(
            "/api/ecommerce-auth/v1/ga/set-default",
            {
              name: accountName,
              property_name: propertyName,
              property_display_name: propertyDisplayName
            }
          )
        ).data,

      sendFacebookCode: async (code: string, state: string) =>
        (
          await this.axios.get<ResponseAPIJSON<BasicSuccessResponse>>(
            `/api/ecommerce-auth/v1/meta/callback?state=${state}&code=${code}`
          )
        ).data,

      sendGa4Code: async (code: string, state: string, scope: string) =>
        (
          await this.axios.get<ResponseAPIJSON<BasicSuccessResponse>>(
            `/api/ecommerce-auth/v1/ga/callback?state=${state}&code=${code}&scope=${scope}`
          )
        ).data
    };
  }

  // eCommerceMetrics API section
  get eCommerceMetrics() {
    return {
      getGABehaviorMetricsComparison: async (
        currentStart: string,
        currentEnd: string,
        previousStart: string,
        previousEnd: string
      ) =>
        (
          await this.axios.get<ResponseAPIJSON<GaBehavioralMetrics>>(
            `/api/ecommerce-metrics/v1/ga/behavior-metrics/comparison?current_start=${currentStart}&current_end=${currentEnd}&previous_start=${previousStart}&previous_end=${previousEnd}`
          )
        ).data,

      getCombinedMetricsComparison: async (
        currentStart: string,
        currentEnd: string,
        previousStart: string,
        previousEnd: string
      ) =>
        (
          await this.axios.get<ResponseAPIJSON<CombinedMetrics>>(
            `/api/ecommerce-metrics/v1/total-metrics/comparison?current_start=${currentStart}&current_end=${currentEnd}&previous_start=${previousStart}&previous_end=${previousEnd}`
          )
        ).data,

      getPerformanceMetrics: async (startDate: string, endDate: string) =>
        (
          await this.axios.get<ResponseAPIJSON<PerformanceMetrics>>(
            `/api/ecommerce-metrics/v1/performance-metrics/daily-report?start_date=${startDate}&end_date=${endDate}`
          )
        ).data,

      getCustomerSnapshot: async (startDate: string, endDate: string) =>
        (
          await this.axios.get<ResponseAPIJSON<CustomerSnapshot>>(
            `/api/ecommerce-metrics/v1/ga/customer-snapshot/report?start_date=${startDate}&end_date=${endDate}`
          )
        ).data,

      getProductMetrics: async (startDate?: string, endDate?: string) => {
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", startDate);
        if (endDate) params.append("end_date", endDate);
        const query = params.toString();
        const url = `/api/ecommerce-metrics/v1/products/metrics${query ? `?${query}` : ""}`;

        return (await this.axios.get<ResponseAPIJSON<ProductMetricsResponse>>(url)).data;
      },

      getMetaAds: async (
        accountId: string,
        limit: number = 100,
        before?: string,
        after?: string
      ) => {
        let params = `?limit=${limit}`;
        if (before) params += `&before=${before}`;
        if (after) params += `&after=${after}`;

        return (
          await this.axios.get<ResponseAPIJSON<MetaAd[]>>(
            `/api/ecommerce-metrics/v1/meta/facebook/${accountId}/ads${params}`
          )
        ).data;
      },

      getFacebookAdAccounts: async () =>
        (
          await this.axios.get<ResponseAPIJSON<FacebookAdsAccount[]>>(
            `/api/ecommerce-metrics/v1/meta/facebook/ads/accounts`
          )
        ).data,
      getFacebookAccountDetails: async (accountId: string) =>
        (
          await this.axios.get<ResponseAPIJSON<FacebookDetails>>(
            `/api/ecommerce-metrics/v1/meta/facebook/${accountId}/details`
          )
        ).data,
      getInstagramAccountDetails: async (accountId: string) =>
        (
          await this.axios.get<ResponseAPIJSON<FacebookDetails>>(
            `/api/ecommerce-metrics/v1/meta/instagram/${accountId}/details`
          )
        ).data,
      getFacebookPostByDate: async (accountId: string, limit: number, from: string, to: string) =>
        (
          await this.axios.get<ResponseAPIJSON<FacebookPost[]>>(
            `/api/ecommerce-metrics/v1/meta/facebook/${accountId}/posts?limit=${limit}&from=${from}&to=${to}`
          )
        ).data,
      getInstagramPostByDate: async (accountId: string, limit: number, from: string, to: string) =>
        (
          await this.axios.get<ResponseAPIJSON<InstagramPost[]>>(
            `/api/ecommerce-metrics/v1/meta/instagram/${accountId}/posts?limit=${limit}&from=${from}&to=${to}`
          )
        ).data,
      getFacebookPosts: async (accountId: string) =>
        (
          await this.axios.get<ResponseAPIJSON<FacebookPost[]>>(
            `/api/ecommerce-metrics/v1/meta/facebook/${accountId}/posts`
          )
        ).data,
      getInstagramPosts: async (accountId: string) =>
        (
          await this.axios.get<ResponseAPIJSON<InstagramPost[]>>(
            `/api/ecommerce-metrics/v1/meta/instagram/${accountId}/posts`
          )
        ).data
    };
  }

  get admin() {
    return {
      getUsers: async () => (await this.axios.get<AdminUsersListResponse>("/api/admin/users")).data,
      getUserDetails: async (userId: string) =>
        (await this.axios.get<AdminUserDetailsResponse>(`/api/admin/users/${userId}`)).data,
      deleteUser: async (userId: string) =>
        (await this.axios.delete<AdminDeleteUserResponse>(`/api/admin/users/${userId}`)).data
    };
  }
}

// API client instance will be created in App.tsx after config is loaded
export let apiClient: ApiClient;

// Initialize API client with config
export const initializeApiClient = (baseUrl: string) => {
  apiClient = new ApiClient(baseUrl);

  return apiClient;
};

// Hook for using API client in React components
export const useApiClient = () => {
  if (!apiClient) {
    throw new Error("API client not initialized. Make sure initializeApiClient() was called.");
  }

  return apiClient;
};
